import { useState, useEffect, useCallback } from 'react';
import { useCopy } from '../../i18n';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationDropdown } from './NotificationDropdown';
import { cn } from '../../lib/utils';

interface NotificationBellProps {
  collapsed?: boolean;
  onNavigateToTask?: (projectId: string, taskId: string) => void;
  /**
   * `icon` desenha só o sino, do tamanho de um botão de ação, para caber ao
   * lado do bloco da conta no rodapé.
   *
   * O sino NÃO entrou no menu da conta junto com Ajuda, Tema e Configurações,
   * e isso é a decisão de produto aqui: contador de não lidas dentro de menu
   * fechado é contador invisível, e este produto avisa prazo, atraso e menção.
   * Um aviso que ninguém vê não é aviso.
   */
  variant?: 'row' | 'icon';
}

export function NotificationBell({ collapsed, onNavigateToTask, variant = 'row' }: NotificationBellProps) {
  const t = useCopy().shell;
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const count = await notificationService.countUnread();
      setUnreadCount(count);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadCount();

    // Subscribe to realtime notifications
    const channel = notificationService.subscribeToNew(user.id, () => {
      setUnreadCount(prev => prev + 1);
    });

    return () => {
      channel.unsubscribe();
    };
  }, [user, loadCount]);

  // Refresh count when popover closes
  useEffect(() => {
    if (!open) {
      loadCount();
    }
  }, [open, loadCount]);

  if (!user) return null;

  // `h-11` e nao `h-9`: no rodape ele divide a faixa com a linha de conta, que
  // tem 44px. Com 36px o sino flutuava no meio, sobrando ar em cima e embaixo
  // que o vizinho nao tinha.
  const buttonClass = variant === 'icon'
    ? "flex shrink-0 items-center justify-center h-11 w-11 rounded-[var(--radius-md)] bg-transparent border-none text-white/70 cursor-pointer transition-colors duration-150 hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]"
    : cn(
        "flex items-center gap-3 w-full bg-transparent border-none text-white/80 text-[length:var(--font-base)] cursor-pointer text-left transition-all duration-150 ease-in-out hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-text)]",
        collapsed ? "justify-center p-2.5" : "px-4 py-2.5"
      );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button className={buttonClass} aria-label={t.nav.notifications}>
              <div className="relative">
                <Bell size={variant === 'icon' ? 18 : 20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] rounded-full bg-[var(--color-danger)] text-white text-[11px] font-bold flex items-center justify-center px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              {!collapsed && variant === 'row' && <span>{t.nav.notifications}</span>}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>Notificações{unreadCount > 0 ? ` (${unreadCount})` : ''}</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="right"
        align="end"
        className="p-0 w-80"
        sideOffset={8}
      >
        <NotificationDropdown
          onNavigateToTask={onNavigateToTask}
          onClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
